import React, { useEffect, useState, useMemo } from "react";
import { X } from 'lucide-react';
import { notificationsAPI } from "../../../api/notificationsApi";
import { teamAPI, manageDealsAPI } from "../../../api/apiHelper";
import { formatNotificationForList, formatNotificationForDetail } from "../../../utils/notificationFormatter";
import { useNavigate } from "react-router-dom";
import useNotificationPoll from "../../../hooks/useNotificationPoll";
import useToast from "../../../hooks/useToast";
import Toast from "../../../components/Toast";
import { socket } from "../../../socket.js";
export default function Notifications() {
  const { toast, showToast, hideToast } = useToast();
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [filterScope, setFilterScope] = useState("all"); // all | mine | team | member
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamFetched, setTeamFetched] = useState(false);
  const [selectedMember, setSelectedMember] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [count, setCount] = useState();
  // const [unreadCount, setUnreadCount] = useState(0);

  // Use count in the UI (for example, show notification count if available)
  // Example: <span>Notifications: {count ?? items.length}</span>
  const navigate = useNavigate();
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  // normalize user id from possible shapes stored in localStorage
  const uidRaw = userData.userId || userData._id || userData.id || (userData.user && (userData.user._id || userData.user.id));
  const uid = uidRaw ? String(uidRaw) : null;
  const userRole = (userData && (userData.role || "")).toLowerCase();

  const load = async () => {
    try {
      setLoading(true);
      const resp = await notificationsAPI.getNotifications();
      const raw = resp?.items || [];
      const cleaned = (raw || [])
        .filter(Boolean)
        .map((it) => (Array.isArray(it) && it.length > 0 && typeof it[0] === "object" ? it[0] : it));
      setItems(cleaned);
    } catch (err) {
      console.error("Failed to load notifications", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // fetch team members once for managers (avoid repeated calls)
  useEffect(() => {
    if (userRole !== "manager" || teamFetched) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await teamAPI.getTeamMembers();
        if (cancelled) return;
        // Accept many possible shapes: res.data.invites, res.data.members, res.data.users, or direct array
        const candidates = (res && res.data && (Array.isArray(res.data.invites) ? res.data.invites : (Array.isArray(res.data.members) ? res.data.members : (Array.isArray(res.data.users) ? res.data.users : (Array.isArray(res.data) ? res.data : [])))))
          || (Array.isArray(res) ? res : []);

        const registered = (Array.isArray(candidates) ? candidates : [])
          .map((inv) => {
            const uid = inv && (inv._id || inv.id || (inv.user && (inv.user._id || inv.user.id)) || inv.userId || (inv.invitee && (inv.invitee._id || inv.invitee.id)) || (inv.member && (inv.member._id || inv.member.id)) || null);
            const name = inv && (
              inv.name ||
              (inv.user && (inv.user.name || inv.user.fullName)) ||
              (inv.invitee && (inv.invitee.name || inv.invitee.fullName)) ||
              inv.fullName ||
              inv.displayName ||
              inv.email ||
              (inv.user && inv.user.email) ||
              (inv.member && (inv.member.name || inv.member.fullName)) ||
              String(uid || "")
            );
            return uid ? { _id: String(uid), name } : null;
          })
          .filter(Boolean);

        // derive members from current notifications as well, to include users referenced only in notifications
        const derivedMap = {};
        if (Array.isArray(items)) {
          for (const n of items) {
            if (!n || !n.data) continue;
            const candIds = [];
            if (n.data.memberId) candIds.push(n.data.memberId);
            if (n.data.userId) candIds.push(n.data.userId);
            if (n.data.assignedTo) candIds.push(n.data.assignedTo);
            if (n.data.assignedBy) candIds.push(n.data.assignedBy);
            if (n.data.createdBy) candIds.push(n.data.createdBy);
            if (n.data.actor) candIds.push(n.data.actor);
            if (n.data.assignedToId) candIds.push(n.data.assignedToId);
            if (n.data.actorId) candIds.push(n.data.actorId);
            if (n.data.createdById) candIds.push(n.data.createdById);
            if (n.data.deletedBy) candIds.push(n.data.deletedBy);
            if (n.data.updatedBy) candIds.push(n.data.updatedBy);

            let id = null;
            for (const c of candIds) {
              if (!c) continue;
              const raw = typeof c === 'string' ? c : (c._id || c.id || null);
              if (raw) { id = raw; break; }
            }
            if (!id) continue;
            const sid = String(id);
            if (derivedMap[sid]) continue;

            const names = [];
            if (n.data.memberName) names.push(n.data.memberName);
            if (n.data.userName) names.push(n.data.userName);
            if (n.data.fullName) names.push(n.data.fullName);
            if (n.data.member) names.push(n.data.member);
            if (n.data.assignedToName) names.push(n.data.assignedToName);
            if (n.data.actorName) names.push(n.data.actorName);
            if (n.data.assignedByName) names.push(n.data.assignedByName);
            if (n.data.createdByName) names.push(n.data.createdByName);
            if (n.data.deletedByName) names.push(n.data.deletedByName);
            if (n.data.updatedByName) names.push(n.data.updatedByName);
            if (n.data.assignedToUser && (n.data.assignedToUser.name || n.data.assignedToUser.fullName)) names.push(n.data.assignedToUser.name || n.data.assignedToUser.fullName);
            if (n.data.actorUser && (n.data.actorUser.name || n.data.actorUser.fullName)) names.push(n.data.actorUser.name || n.data.actorUser.fullName);
            if (n.data.createdByUser && (n.data.createdByUser.name || n.data.createdByUser.fullName)) names.push(n.data.createdByUser.name || n.data.createdByUser.fullName);

            const name = names.find(Boolean) || sid;
            derivedMap[sid] = { _id: sid, name };
          }
        }

        // merge registered + derived, dedupe by _id
        const mergedMap = {};
        for (const r of registered) {
          if (!r || !r._id) continue;
          mergedMap[String(r._id)] = r;
        }
        for (const k of Object.keys(derivedMap)) {
          if (!mergedMap[k]) mergedMap[k] = derivedMap[k];
        }
        const merged = Object.values(mergedMap);
        if (!cancelled) setTeamMembers(merged);
      } catch (e) {
        // ignore errors but still mark fetched so we don't retry aggressively
      } finally {
        if (!cancelled) setTeamFetched(true);
      }
    })();
    return () => { cancelled = true; };
  }, [userRole, teamFetched, items]);

  // Fallback: if team API didn't return members, derive a minimal member list
  // from notifications so the "Specific member" dropdown isn't empty.
  useEffect(() => {
    if (userRole !== 'manager' || teamMembers.length > 0) return;
    if (!Array.isArray(items) || items.length === 0) return;
    const map = {};
    for (const n of items) {
      if (!n || !n.data) continue;
      // collect candidate ids from common fields and backend-enriched suffixes
      const candIds = [];
      if (n.data.memberId) candIds.push(n.data.memberId);
      if (n.data.userId) candIds.push(n.data.userId);
      if (n.data.assignedTo) candIds.push(n.data.assignedTo);
      if (n.data.assignedBy) candIds.push(n.data.assignedBy);
      if (n.data.createdBy) candIds.push(n.data.createdBy);
      if (n.data.actor) candIds.push(n.data.actor);
      if (n.data.memberIdId) candIds.push(n.data.memberIdId);
      if (n.data.assignedToId) candIds.push(n.data.assignedToId);
      if (n.data.actorId) candIds.push(n.data.actorId);
      if (n.data.createdById) candIds.push(n.data.createdById);
      if (n.data.deletedBy) candIds.push(n.data.deletedBy);
      if (n.data.updatedBy) candIds.push(n.data.updatedBy);

      let id = null;
      for (const c of candIds) {
        if (!c) continue;
        const raw = typeof c === 'string' ? c : (c._id || c.id || null);
        if (raw) { id = raw; break; }
      }
      if (!id) continue;
      const sid = String(id);
      if (map[sid]) continue;

      // derive name: prefer enriched fields when present
      const names = [];
      if (n.data.memberName) names.push(n.data.memberName);
      if (n.data.userName) names.push(n.data.userName);
      if (n.data.fullName) names.push(n.data.fullName);
      if (n.data.member) names.push(n.data.member);
      if (n.data.memberIdName) names.push(n.data.memberIdName);
      if (n.data.assignedToName) names.push(n.data.assignedToName);
      if (n.data.actorName) names.push(n.data.actorName);
      if (n.data.assignedByName) names.push(n.data.assignedByName);
      if (n.data.createdByName) names.push(n.data.createdByName);
      if (n.data.deletedByName) names.push(n.data.deletedByName);
      if (n.data.updatedByName) names.push(n.data.updatedByName);
      if (n.data.memberIdUser && (n.data.memberIdUser.name || n.data.memberIdUser.fullName)) names.push(n.data.memberIdUser.name || n.data.memberIdUser.fullName);
      if (n.data.assignedToUser && (n.data.assignedToUser.name || n.data.assignedToUser.fullName)) names.push(n.data.assignedToUser.name || n.data.assignedToUser.fullName);
      if (n.data.actorUser && (n.data.actorUser.name || n.data.actorUser.fullName)) names.push(n.data.actorUser.name || n.data.actorUser.fullName);
      if (n.data.assignedByUser && (n.data.assignedByUser.name || n.data.assignedByUser.fullName)) names.push(n.data.assignedByUser.name || n.data.assignedByUser.fullName);
      if (n.data.createdByUser && (n.data.createdByUser.name || n.data.createdByUser.fullName)) names.push(n.data.createdByUser.name || n.data.createdByUser.fullName);
      if (n.data.deletedByUser && (n.data.deletedByUser.name || n.data.deletedByUser.fullName)) names.push(n.data.deletedByUser.name || n.data.deletedByUser.fullName);
      if (n.data.updatedByUser && (n.data.updatedByUser.name || n.data.updatedByUser.fullName)) names.push(n.data.updatedByUser.name || n.data.updatedByUser.fullName);

      const name = names.find(Boolean) || sid;
      map[sid] = { _id: sid, name };
    }
    const derived = Object.values(map);
    if (derived.length > 0) setTeamMembers(derived);
  }, [userRole, teamMembers.length, items]);

  // client-side filtering to support manager scopes without relying on backend params
  const notifTargetsUser = (notif, testUid) => {
    if (!notif || !testUid) return false;
    const candidates = [];
    if (notif.user) candidates.push(notif.user);
    if (notif.userId) candidates.push(notif.userId);
    if (notif.createdBy) candidates.push(notif.createdBy);
    if (notif.to) candidates.push(notif.to);
    if (notif.for) candidates.push(notif.for);
    if (notif.assignedTo) candidates.push(notif.assignedTo);
    if (notif.data && notif.data.assignedTo) candidates.push(notif.data.assignedTo);
    if (notif.data && notif.data.assignedUser) candidates.push(notif.data.assignedUser);
    if (notif.data && notif.data.userId) candidates.push(notif.data.userId);
    if (notif.data && notif.data.memberId) candidates.push(notif.data.memberId);
    if (notif.data && notif.data.assignedTo && typeof notif.data.assignedTo === "object") candidates.push(notif.data.assignedTo._id || notif.data.assignedTo.id);
    if (notif.data && notif.data.assignedBy) candidates.push(notif.data.assignedBy);
    if (notif.data && notif.data.assignedByUser && typeof notif.data.assignedByUser === "object") candidates.push(notif.data.assignedByUser._id || notif.data.assignedByUser.id);
    if (notif.data && notif.data.createdBy) candidates.push(notif.data.createdBy);
    if (notif.data && notif.data.createdByUser && typeof notif.data.createdByUser === "object") candidates.push(notif.data.createdByUser._id || notif.data.createdByUser.id);
    if (notif.data && notif.data.deletedBy) candidates.push(notif.data.deletedBy);
    if (notif.data && notif.data.updatedBy) candidates.push(notif.data.updatedBy);
    for (const c of candidates) {
      if (!c) continue;
      const cand = typeof c === "string" ? c : (c._id || c.id || c.user || c.userId);
      if (cand && String(cand) === String(testUid)) return true;
    }
    return false;
  };

  useEffect(() => {
    try {
      let list = Array.isArray(items) ? items.slice() : [];
      if (filterScope === "mine" && uid) {
        list = list.filter((n) => notifTargetsUser(n, uid));
      } else if (filterScope === "member" && selectedMember) {
        list = list.filter((n) => notifTargetsUser(n, selectedMember));
      } else if (filterScope === "team") {
        const memberIds = (teamMembers || []).map((m) => String(m._id));
        list = list.filter((n) => {
          const targetsAny = memberIds.some((id) => notifTargetsUser(n, id));
          if (targetsAny) return true;
          const hasSpecific = !!(n.user || n.userId || n.createdBy || n.to || n.for || n.assignedTo || (n.data && (n.data.assignedTo || n.data.userId || n.data.memberId)));
          return !hasSpecific;
        });
      }
      setFilteredItems(list);
    } catch (e) {
      setFilteredItems(items || []);
    }
  }, [items, filterScope, selectedMember, teamMembers, uid]);


  const isReadByUser = (notif) => {
    try {
      if (!notif || !Array.isArray(notif.readBy) || !uid) return false;
      return notif.readBy.some((r) => {
        if (!r) return false;
        const candidate = typeof r === "string" ? r : (r.user || r._id || r.id);
        return candidate && String(candidate) === String(uid);
      });
    } catch (e) {
      return false;
    }
  };

  const markAsReadLocally = (id) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it._id !== id) return it;
        const already = Array.isArray(it.readBy) ? it.readBy.slice() : [];
        const has = already.some((r) => {
          if (!r) return false;
          const cand = typeof r === "string" ? r : (r.user || r._id || r.id);
          return cand && String(cand) === String(uid);
        });
        if (!has && uid) {
          already.push({ user: uid, role: userData.role || null, readAt: new Date().toISOString() });
        }
        return { ...it, readBy: already };
      })
    );
  };

  const handleMarkRead = async (id) => {
    try {
      // optimistic local update
      markAsReadLocally(id);
      // call server
      const resp = await notificationsAPI.markRead(id);
      // if server returned updated notification, replace it in list
      const serverNotif = resp?.data?.notification || resp?.notification || null;
      if (serverNotif) {
        setItems((prev) => prev.map((it) => (it._id === id ? { ...it, ...serverNotif } : it)));
      }
      // notify other tabs (will trigger polling hook there)
      try {
        localStorage.setItem("notifications:msg", JSON.stringify({ kind: "request-refresh", t: Date.now() }));
      } catch (e) { }
      // refresh local poll immediately if available
      try {
        if (typeof refreshUnread === "function") await refreshUnread();
      } catch (e) { }
    } catch (err) {
      console.error("Failed to mark read", err);
    }
  };


  const handleOpen = async (it) => {
    setSelected({ ...it, __rawLink: it.link || null });
    setShowModal(true);
    if (!isReadByUser(it) && uid) {
      await handleMarkRead(it._id);
    }
  };

  const handleOpenAction = (it) => {
    setSelected({ ...it, type: 'action' });
    setShowModal(true);
  };

  const handleApprove = async () => {
    if (!selected) return;
    try {
      const res = await manageDealsAPI.approvePhoneChange(selected.notificationId || selected._id);
      if (res && res.success) {
        showToast("Approved successfully", "success");
        setShowModal(false);
        // Refresh action list
        const list = await manageDealsAPI.getPendingPhoneApprovals();
        if (list && list.pendingApprovals) setPendingActions(list.pendingApprovals);
        else setPendingActions([]);
      }
    } catch (e) {
      console.error("Approval failed", e);
      showToast("Failed to approve", "error");
    }
  };

  const handleReject = async () => {
    if (!selected) return;
    const reason = window.prompt("Reason for rejection:");
    if (!reason) return;
    try {
      const res = await manageDealsAPI.rejectPhoneChange(selected.notificationId || selected._id, reason);
      if (res && res.success) {
        showToast("Rejected successfully", "success");
        setShowModal(false);
        // Refresh action list
        const list = await manageDealsAPI.getPendingPhoneApprovals();
        if (list && list.pendingApprovals) setPendingActions(list.pendingApprovals);
        else setPendingActions([]);
      }
    } catch (e) {
      console.error("Rejection failed", e);
      showToast("Failed to reject", "error");
    }
  };
  const closeModal = () => {
    setShowModal(false);
    setSelected(null);
  };

  // polling hook will be declared just before the JSX return
  // use polling hook to keep unread count updated across tabs
  const { unreadCount: polledUnreadCount, refresh: refreshUnread } = useNotificationPoll({ intervalMs: 30 * 1000 });
  // local override: set to 0 immediately after markAllRead, reset when poll returns a real value
  const [unreadOverride, setUnreadOverride] = useState(null);
  const displayUnreadCount = unreadOverride !== null ? unreadOverride : polledUnreadCount;

  // categorize notifications into groups like system, task, deal, lead, other
  const categorize = (notif) => {
    const t = (notif && notif.type) || "";
    if (!t) return "other";
    const lower = t.toLowerCase();
    if (lower.startsWith("task") || lower.startsWith("task.")) return "task";
    if (lower.startsWith("deal") || lower.startsWith("deal.")) return "deal";
    if (lower.includes("lead")) return "lead";
    if (lower.startsWith("stage") || lower.startsWith("stage.")) return "stage";
    if (lower.startsWith("system") || lower.includes("error") || lower.includes("fail")) return "system";
    return "other";
  };

  const groups = useMemo(() => {
    const g = { all: [] };
    for (const it of filteredItems.length ? filteredItems : items) {
      g.all.push(it);
      const k = categorize(it);
      if (!g[k]) g[k] = [];
      g[k].push(it);
    }
    return g;
  }, [filteredItems, items]);

  const [pendingActions, setPendingActions] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    if (activeCategory === 'action') {
      const fetchActions = async () => {
        try {
          const res = await manageDealsAPI.getPendingPhoneApprovals();
          if (res && res.pendingApprovals) {
            setPendingActions(res.pendingApprovals);
          } else {
            setPendingActions([]);
          }
        } catch (e) {
          console.error("Failed to load pending actions", e);
        }
      };
      fetchActions();
    }
  }, [activeCategory]);
  const displayList = (filterScope !== 'all') ? filteredItems : items;
  const showEmpty = !Array.isArray(displayList) || displayList.length === 0;
  // uid is already derived from localStorage above — do NOT hardcode this
  const socketUserId = uid;

  useEffect(() => {
    if (!socketUserId) return; // don't connect without a valid user id

    // Refresh token in case it was set after module import
    const currentToken = localStorage.getItem("authToken");
    if (currentToken) {
      socket.auth = { token: currentToken };
    }

    // Refresh auth token before connecting (in case module was imported before login)

    if (currentToken) socket.auth = { token: currentToken };

    if (!socket.connected) {
      console.log('[Notifications] connecting socket for userId:', socketUserId);
      socket.connect();
    }

    // 🔥 Live: new notification arrived — reload list
    const handleNewNotification = (data) => {
      console.log('[Notifications] socket new_notification received:', data);
      load(); // refresh full list from server
    };

    // 🔥 Live: unread count update
    const handleUnread = ({ count }) => {
      console.log('[Notifications] socket unread_count received:', count);
      setCount(count);
    };

    socket.on("new_notification", handleNewNotification);
    socket.on("unread_count", handleUnread);
      // cleanup — remove only the listeners we added, do not disconnect shared socket
    return () => {
      socket.off("new_notification", handleNewNotification);
      socket.off("unread_count", handleUnread);
    };
  }, [socketUserId]);


  return (
    <div style={ { maxWidth: 900, margin: "12px auto", padding: "0 12px" } }>
      <div style={ { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 } }>
        <h2 style={ { margin: 0 } }>Notifications</h2>
        <div style={ { display: "flex", gap: 8, alignItems: "center" } }>
         {/* <button onClick={() => socket.emit("message", "Hello from React")}>
            Send Message
          </button> */}
          <button
            onClick={ () => navigate(-1) }
            style={ {
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: '#ffffff',
              border: '1px solid #d1d5db',
              padding: '6px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              color: '#374151',
              fontSize: '13px',
              fontWeight: 500,
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              transition: 'all 0.2s ease'
            } }
          >
            <X size={ 16 } />
            Close
          </button>
        </div>
      </div>

      { loading ? (
        <div>Loading...</div>
      ) : showEmpty ? (
        <div style={ {
          padding: '60px 20px',
          textAlign: 'center',
          background: '#f9fafb',
          borderRadius: 8,
          border: '1px dashed #d1d5db',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
          marginTop: 20
        } }>
          { filterScope === 'member' && selectedMember ? (
            <>
              <div style={ { fontSize: 14, fontWeight: 500, color: '#374151' } }>
                No notifications found for this member
              </div>
              <div style={ { fontSize: 13, color: '#6b7280', maxWidth: 300 } }>
                Try selecting a different member or view all notifications.
              </div>
              <button
                onClick={ () => {
                  setFilterScope('all');
                  setSelectedMember('');
                } }
                style={ {
                  marginTop: 8,
                  background: '#fff',
                  border: '1px solid #d1d5db',
                  padding: '8px 16px',
                  borderRadius: 6,
                  color: '#374151',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                } }
              >
                Clear Member Filter
              </button>
            </>
          ) : (
            <div style={ { color: "#6b7280", fontWeight: 500 } }>No notifications</div>
          ) }
        </div>
      ) : (
        <div>
          {/* Manager filters: view all / mine / team / specific member */ }
          { userRole === 'manager' && (
            <div style={ { display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' } }>
              <label style={ { color: '#111827', fontSize: 13, fontWeight: 500 } }>Show:</label>

              {/* Primary Scope Filter */ }
              <select
                value={ filterScope }
                onChange={ (e) => {
                  setFilterScope(e.target.value);
                  setSelectedMember('');
                } }
                style={ { padding: '6px 8px', borderRadius: 6, borderColor: '#d1d5db', color: '#111827' } }
              >
                <option value="all">All notifications</option>
                <option value="mine">My notifications</option>
                <option value="team">Team notifications</option>
                <option value="member" style={ { display: 'none' } }>Specific member</option>
              </select>

              {/* Member Filter - Always visible or conditioned? User said "beside", implying visibility. */ }
              {/* We show it always for managers to easily pick. */ }
              <select
                value={ selectedMember }
                onChange={ (e) => {
                  if (e.target.value) {
                    setFilterScope('member');
                    setSelectedMember(e.target.value);
                  } else {
                    // if cleared, revert to all
                    setFilterScope('all');
                    setSelectedMember('');
                  }
                } }
                style={ {
                  padding: '6px 8px',
                  borderRadius: 6,
                  borderColor: '#d1d5db',
                  color: '#111827'
                } }
              >
                <option value="">Select Member</option>
                { (teamMembers || []).map((m) => (
                  <option key={ m._id } value={ m._id }>{ m.name }</option>
                )) }
              </select>
            </div>
          ) }

          {/* Category tabs */ }
          <div style={ { display: "flex", gap: 8, marginBottom: 12, alignItems: 'center', overflowX: 'auto' } }>
            { [
              // { key: "action", label: `Action (${pendingActions.length})` },
              { key: "all", label: `All (${groups.all.length})` },
              { key: "system", label: `System (${(groups.system || []).length})` },
              { key: "task", label: `Task (${(groups.task || []).length})` },
              { key: "deal", label: `Deal (${(groups.deal || []).length})` },
              { key: "lead", label: `Lead (${(groups.lead || []).length})` },
              { key: "stage", label: `Stage (${(groups.stage || []).length})` },
              { key: "other", label: `Other (${(groups.other || []).length})` },
            ].map((tab) => (
              <button
                key={ tab.key }
                onClick={ () => setActiveCategory(tab.key) }
                style={ {
                  padding: '8px 12px',
                  borderRadius: 6,
                  border: activeCategory === tab.key ? '1px solid #1976d2' : '1px solid #e8e8e8',
                  background: activeCategory === tab.key ? '#e8f0ff' : '#fff',
                  cursor: 'pointer'
                } }
              >
                { tab.label }
              </button>
            )) }

            <div style={ { marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center' } }>
              { displayUnreadCount > 0 && (
                <div style={ {
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#fff1f2',
                  color: '#e11d48',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: 600,
                  border: '1px solid #fecdd3'
                } }>
                  <div style={ { width: '8px', height: '8px', borderRadius: '50%', background: '#e11d48' } }></div>
                  { displayUnreadCount } Unread
                </div>
              ) }
              <button onClick={ async () => {
                // optimistically mark all items as read locally and reset unread count
                setUnreadOverride(0);
                setItems((prev) => prev.map((it) => {
                  const already = Array.isArray(it.readBy) ? it.readBy.slice() : [];
                  const has = already.some((r) => {
                    if (!r) return false;
                    const cand = typeof r === "string" ? r : (r.user || r._id || r.id);
                    return cand && String(cand) === String(uid);
                  });
                  if (!has && uid) {
                    already.push({ user: uid, role: userData.role || null, readAt: new Date().toISOString() });
                  }
                  return { ...it, readBy: already, clientIsRead: true };
                }));
                try {
                  const typeParam = activeCategory === 'all' ? null : activeCategory;
                  await notificationsAPI.markAllRead(typeParam);
                  await load();
                  try { localStorage.setItem('notifications:msg', JSON.stringify({ kind: 'request-refresh', t: Date.now() })); } catch (e) { }
                  try { if (typeof refreshUnread === 'function') await refreshUnread(); } catch (e) { }
                  // after server confirms, clear override so poll takes over
                  setUnreadOverride(null);
                } catch (e) {
                  console.error('markAllRead failed', e);
                  setUnreadOverride(null);
                }
              } } style={ { background: '#1976d2', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: 6, cursor: 'pointer' } }>Mark all read</button>
            </div>
          </div>

          <div style={ { display: "grid", gap: 12 } }>
            { activeCategory === 'action' ? (
              (pendingActions.length === 0) ? (
                <div style={ { textAlign: 'center', padding: 20, color: '#666' } }>No pending actions</div>
              ) : (
                pendingActions.map(it => (
                  <div
                    key={ it.notificationId || it._id }
                    onClick={ () => handleOpenAction(it) }
                    style={ {
                      padding: 20, // Larger padding
                      borderRadius: 8,
                      background: '#fff',
                      border: '1px solid #d1d5db',
                      cursor: 'pointer',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                      borderLeft: '4px solid #f59e0b' // Distinct visual indicator
                    } }
                  >
                    <div style={ { fontWeight: 600, fontSize: 16, marginBottom: 4 } }>Phone Update Request</div>
                    <div style={ { color: '#374151', marginBottom: 4 } }>
                      Deal: <strong>{ it.dealTitle }</strong> • Sales: { it.salesPersonName }
                    </div>
                    <div style={ { color: '#6b7280', fontSize: 13 } }>
                      Requested: { new Date(it.createdAt).toLocaleString() }
                    </div>
                  </div>
                ))
              )
            ) : (
              (groups[activeCategory] || []).map((it) => {
                const read = isReadByUser(it) || Boolean(it.clientIsRead);
                return (
                  <div
                    key={ it._id }
                    onClick={ () => handleOpen(it) }
                    role="button"
                    tabIndex={ 0 }
                    style={ {
                      padding: 12,
                      borderRadius: 8,
                      background: read ? "#fff" : "#f8fbff",
                      border: read ? "1px solid #f0f0f0" : "1px solid #dfeeff",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                    } }
                  >
                    <div style={ { flex: 1 } }>
                      { (() => {
                        const f = formatNotificationForList(it);
                        return (
                          <>
                            <div style={ { fontWeight: read ? 400 : 600 } }>{ f.summary }</div>
                            <div style={ { color: "#666", fontSize: 13 } }>{/* optional short text if available */ }</div>
                            <div style={ { color: "#999", fontSize: 12, marginTop: 6 } }>{ f.time }</div>
                          </>
                        );
                      })() }
                    </div>
                    <div style={ { display: "flex", gap: 8, alignItems: "center" } }>
                      { !read ? (
                        <span style={ { background: "#e53e3e", color: "#fff", padding: "4px 8px", borderRadius: 12, fontSize: 12 } }>Unread</span>
                      ) : (
                        <span style={ { background: "#f0f0f0", color: "#666", padding: "4px 8px", borderRadius: 12, fontSize: 12 } }>Read</span>
                      ) }
                      { !read && (
                        <button onClick={ (e) => { e.stopPropagation(); handleMarkRead(it._id); } } style={ { background: "#1976d2", color: "#fff", border: "none", padding: "6px 10px", borderRadius: 6, cursor: "pointer" } }>Mark read</button>
                      ) }
                    </div>
                  </div>
                );
              })
            ) }
          </div>
        </div>
      ) }

      { showModal && selected && (
        <div style={ { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 } } onClick={ closeModal }>
          <div onClick={ (e) => e.stopPropagation() } style={ { background: "#fff", padding: 20, borderRadius: 8, maxWidth: 720, width: "100%" } }>
            { selected.type === 'action' ? (
              <div>
                <div style={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, borderBottom: '1px solid #eee', paddingBottom: 10 } }>
                  <h2 style={ { margin: 0, color: '#d97706' } }>Update Request Approval</h2>
                  <button onClick={ closeModal } style={ { background: "transparent", border: "none", cursor: "pointer", color: "#666" } }><X size={ 20 } /></button>
                </div>

                <div style={ { display: 'grid', gap: 16, marginBottom: 24 } }>
                  <div style={ { padding: 12, background: '#f9fafb', borderRadius: 6 } }>
                    <div style={ { fontSize: 12, color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 } }>Deal Details</div>
                    <div style={ { fontSize: 16, fontWeight: 500, marginTop: 4 } }>{ selected.dealTitle }</div>
                    <div style={ { fontSize: 14, color: '#374151' } }>Sales Person: <strong>{ selected.salesPersonName }</strong></div>
                    <div style={ { fontSize: 14, color: '#374151' } }>Lead Source: { selected.leadSource || 'N/A' }</div>
                  </div>

                  <div style={ { padding: 12, background: '#fffbeb', borderRadius: 6, border: '1px solid #fcd34d' } }>
                    <div style={ { fontSize: 12, color: '#92400e', textTransform: 'uppercase', fontWeight: 600 } }>Update Request</div>
                    <div style={ { display: 'flex', gap: 20, marginTop: 8 } }>
                      <div>
                        <div style={ { fontSize: 12, color: '#92400e' } }>Old Number</div>
                        <div style={ { fontSize: 15, fontWeight: 500, textDecoration: 'line-through', color: '#6b7280' } }>{ selected.oldPhoneNumber }</div>
                      </div>
                      <div>
                        <div style={ { fontSize: 12, color: '#92400e' } }>New Number</div>
                        <div style={ { fontSize: 15, fontWeight: 600, color: '#059669' } }>{ selected.newPhoneNumber }</div>
                      </div>
                    </div>
                    <div style={ { marginTop: 12, fontSize: 14, color: '#374151' } }>
                      <strong>Reason:</strong> { selected.reason }
                    </div>
                  </div>
                </div>

                <div style={ { display: 'flex', gap: 12, justifyContent: 'flex-end' } }>
                  <button onClick={ handleReject } style={ { padding: '10px 20px', borderRadius: 6, border: '1px solid #ef4444', color: '#ef4444', background: '#fff', cursor: 'pointer', fontWeight: 500 } }>
                    Decline
                  </button>
                  <button onClick={ handleApprove } style={ { padding: '10px 20px', borderRadius: 6, border: 'none', background: '#059669', color: '#fff', cursor: 'pointer', fontWeight: 500 } }>
                    Approve Update
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div style={ { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 } }>
                  <h3 style={ { margin: 0 } }>{ formatNotificationForDetail(selected).title }</h3>
                  <div style={ { display: "flex", gap: 8 } }>
                    <button onClick={ closeModal } style={ { background: "transparent", border: "none", cursor: "pointer", color: "#666" } }>Close</button>
                  </div>
                </div>
                <div style={ { color: "#333", marginBottom: 8 } }>
                  { (() => {
                    const det = formatNotificationForDetail(selected);
                    return (
                      <div>
                        { det.rows && det.rows.map((r, idx) => (
                          <div key={ idx } style={ { marginBottom: 6 } }>
                            <div style={ { fontSize: 13, color: '#666' } }>{ r.label }:</div>
                            <div style={ { fontSize: 15, color: '#111' } }>{ r.value }</div>
                          </div>
                        )) }

                        { det.changeList && det.changeList.length > 0 && (
                          <div style={ { marginTop: 8 } }>
                            <div style={ { fontSize: 13, color: '#666' } }>Summary of changes:</div>
                            <ul style={ { marginTop: 6 } }>
                              { det.changeList.map((c, i) => (
                                <li key={ i } style={ { color: '#111' } }>{ c }</li>
                              )) }
                            </ul>
                          </div>
                        ) }

                        { det.ctaLabel && det.ctaLink && (
                          <div style={ { marginTop: 12 } }>
                            <button onClick={ () => { closeModal(); navigate(det.ctaLink); } } style={ { background: '#1976d2', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: 6, cursor: 'pointer' } }>{ det.ctaLabel }</button>
                          </div>
                        ) }
                      </div>
                    );
                  })() }
                </div>
              </>
            ) }
          </div>
        </div>
      ) }
      <Toast { ...toast } onClose={ hideToast } />
    </div>
  );
}

